/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import UserHistory from '../../../../domain/models/user_doc/UserHistory';
import getUserHistory from '../../../../domain/use_cases/users/getUserHistory';

export const USER_HISTORY_FETCH_LIMIT = 10;

const UserHistoryContext = createContext({
  count: 0,
  hitsPerPage: USER_HISTORY_FETCH_LIMIT,
  items: [] as UserHistory[],
  lastVisible: undefined as QueryDocumentSnapshot<DocumentData, DocumentData> | undefined,
  scopedUserUid: null as string | null,
  fetch: (_: string | null, __?: number) => {},
  fetchNext: () => {},
  reset: () => {},
  status: ContextStatus.IDLE,
});

export const UserHistoryProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(USER_HISTORY_FETCH_LIMIT);
  const [items, setItems] = useState([] as UserHistory[]);
  const [lastVisible, setLastVisible] = useState<
    QueryDocumentSnapshot<DocumentData, DocumentData> | undefined
  >(undefined);
  const [scopedUserUid, setScopedUserUid] = useState(null as string | null);
  const [status, setStatus] = useState(ContextStatus.IDLE);

  const fetch = async (userUid: string | null, limit?: number) => {
    const effectiveLimit = limit ?? hitsPerPage;
    if (limit !== undefined) setHitsPerPage(limit);
    setScopedUserUid(userUid);
    setStatus(ContextStatus.LOADING);
    try {
      if (!userUid) {
        setItems([]);
        setCount(0);
        setLastVisible(undefined);
        setStatus(ContextStatus.SUCCESS);
        return;
      }
      const useCaseResponse = await getUserHistory(
        userUid,
        effectiveLimit
      );
      setItems(useCaseResponse.items);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNext = async () => {
    if (!scopedUserUid || !lastVisible) return;
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getUserHistory(
        scopedUserUid,
        hitsPerPage,
        lastVisible
      );
      setItems((prev) => [...prev, ...useCaseResponse.items]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setCount(0);
    setHitsPerPage(USER_HISTORY_FETCH_LIMIT);
    setItems([]);
    setLastVisible(undefined);
    setScopedUserUid(null);
    setStatus(ContextStatus.IDLE);
  };

  return (
    <UserHistoryContext.Provider
      value={{
        count,
        hitsPerPage,
        items,
        lastVisible,
        scopedUserUid,
        fetch,
        fetchNext,
        reset,
        status,
      }}
    >
      {children}
    </UserHistoryContext.Provider>
  );
};

export default UserHistoryContext;
